import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Grid,
    Group,
    Loader,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import { getAuthHeaders } from "../../../api/authHelper";
import HeroSectionV2 from '../../../components/HeroSectionV2';
import Billede1 from '../../../Images/Billede1.jpg';

interface FormPracticeDto {
    practiceId?: number;
    country?: string;
    practiceDate?: string;
    practiceTime?: number;
    numberOfCourts?: number;
    numberOfPlayers?: number;
    numberOfMinuts?: number;
    createdDate?: string;
    eventId?: number;
    userId?: number;
}

interface OptionDto {
    value: number;
    text: string;
}

interface EventDto {
    eventID: number;
    eventName: string;
    startTime?: string;
    endTime?: string;
}

type GroupedPractice = {
    key: string;
    date: string;
    timeValue?: number;
    timeText: string;
    bookings: FormPracticeDto[];
    totalCourts: number;
    totalPlayers: number;
};

const FormPracticeAdminPage: React.FC = () => {
    const apiBase = process.env.REACT_APP_API_URL;

    const PRACTICE_URL = `${apiBase}/api/FormPractice`;
    const PRACTICE_TIME_URL = `${apiBase}/api/Option_PracticeTime`;
    const PRACTICE_MINUTES_URL = `${apiBase}/api/Option_PracticeMinuts`;

    const EVENTS_URL = `${apiBase}/api/Events`;

    const [events, setEvents] = useState<EventDto[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

    const [practices, setPractices] = useState<FormPracticeDto[]>([]);
    const [practiceTimes, setPracticeTimes] = useState<OptionDto[]>([]);
    const [practiceMinutes, setPracticeMinutes] = useState<OptionDto[]>([]);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingPractices, setLoadingPractices] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filtre
    const [countryFilter, setCountryFilter] = useState("");
    const [dateFilter, setDateFilter] = useState<string | null>(null);
    const [timeFilter, setTimeFilter] = useState<string | null>(null);

    // ---------------------------------------------------------
    // HELPERS
    // ---------------------------------------------------------

    const normalizeDate = (date?: string) => {
        if (!date) return "";

        if (date.includes("T")) {
            return date.split("T")[0];
        }

        return date;
    };

    const formatDate = (date?: string) => {
        const normalized = normalizeDate(date);

        if (!normalized) return "—";

        const value = new Date(`${normalized}T00:00:00`);

        if (Number.isNaN(value.getTime())) {
            return normalized;
        }

        return value.toLocaleDateString("da-DK", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const getPracticeTimeText = (value?: number) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceTimes.find((x) => x.value === value)?.text ??
            String(value)
        );
    };

    const getPracticeMinutesText = (value?: number) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceMinutes.find((x) => x.value === value)?.text ??
            `${value} min.`
        );
    };

    const getMinutesNumericValue = (value?: number) => {
        if (value === undefined || value === null) {
            return 0;
        }

        /*
         * Hvis NumberOfMinuts allerede ER antal minutter,
         * returnerer vi bare value.
         *
         * Hvis det i jeres database er et Option-ID, kan
         * dette senere ændres til at læse minutværdien
         * ud af option-teksten.
         */
        return value;
    };

    // ---------------------------------------------------------
    // LOAD INITIAL DATA
    // ---------------------------------------------------------

    useEffect(() => {
        fetchEvents();
        fetchPracticeTimes();
        fetchPracticeMinutes();
    }, []);

    useEffect(() => {
        if (!selectedEventId) {
            setPractices([]);
            return;
        }

        fetchPractices(Number(selectedEventId));
    }, [selectedEventId]);

    const fetchEvents = async () => {
        try {
            setLoadingEvents(true);
            setError(null);

            const res = await fetch(EVENTS_URL, {
                headers: getAuthHeaders() as Record<string, string>,
            });

            if (!res.ok) {
                throw new Error(
                    `Kunne ikke hente events (${res.status})`
                );
            }

            const data = await res.json();

            /*
             * Hvis endpointet returnerer direkte array:
             * [
             *   { eventID: 1, eventName: "Denmark Open" }
             * ]
             */
            const eventList: EventDto[] = Array.isArray(data)
                ? data
                : data.items ?? data.events ?? [];

            setEvents(eventList);

            // Vælg første event automatisk
            if (eventList.length > 0) {
                setSelectedEventId(String(eventList[0].eventID));
            }
        } catch (err) {
            console.error(err);
            setError("Kunne ikke hente events.");
        } finally {
            setLoadingEvents(false);
        }
    };

    const fetchPracticeTimes = async () => {
        try {
            const res = await fetch(PRACTICE_TIME_URL, {
                headers: getAuthHeaders() as Record<string, string>,
            });

            if (!res.ok) {
                return;
            }

            const data = await res.json();
            setPracticeTimes(data);
        } catch (err) {
            console.error("PracticeTime fejl:", err);
        }
    };

    const fetchPracticeMinutes = async () => {
        try {
            const res = await fetch(PRACTICE_MINUTES_URL, {
                headers: getAuthHeaders() as Record<string, string>,
            });

            if (!res.ok) {
                return;
            }

            const data = await res.json();
            setPracticeMinutes(data);
        } catch (err) {
            console.error("PracticeMinutes fejl:", err);
        }
    };

    const fetchPractices = async (eventId: number) => {
        try {
            setLoadingPractices(true);
            setError(null);

            const res = await fetch(
                `${PRACTICE_URL}/admin?eventId=${eventId}`,
                {
                    headers: getAuthHeaders() as Record<string, string>,
                }
            );

            if (!res.ok) {
                throw new Error(
                    `Kunne ikke hente træningsbookinger (${res.status})`
                );
            }

            const data: FormPracticeDto[] = await res.json();

            setPractices(data);
        } catch (err) {
            console.error(err);
            setError("Kunne ikke hente træningsbookinger.");
            setPractices([]);
        } finally {
            setLoadingPractices(false);
        }
    };

    // ---------------------------------------------------------
    // AVAILABLE DATES
    // ---------------------------------------------------------

    const availableDates = useMemo(() => {
        const dates = Array.from(
            new Set(
                practices
                    .map((x) => normalizeDate(x.practiceDate))
                    .filter(Boolean)
            )
        );

        return dates
            .sort()
            .map((date) => ({
                value: date,
                label: formatDate(date),
            }));
    }, [practices]);

    // ---------------------------------------------------------
    // FILTER
    // ---------------------------------------------------------

    const filteredPractices = useMemo(() => {
        const countrySearch = countryFilter
            .trim()
            .toLowerCase();

        return practices
            .filter((practice) => {
                if (
                    countrySearch &&
                    !practice.country
                        ?.toLowerCase()
                        .includes(countrySearch)
                ) {
                    return false;
                }

                if (
                    dateFilter &&
                    normalizeDate(practice.practiceDate) !== dateFilter
                ) {
                    return false;
                }

                if (
                    timeFilter &&
                    String(practice.practiceTime) !== timeFilter
                ) {
                    return false;
                }

                return true;
            })
            .sort((a, b) => {
                const dateA = normalizeDate(a.practiceDate);
                const dateB = normalizeDate(b.practiceDate);

                if (dateA !== dateB) {
                    return dateA.localeCompare(dateB);
                }

                return (a.practiceTime ?? 0) - (b.practiceTime ?? 0);
            });
    }, [
        practices,
        countryFilter,
        dateFilter,
        timeFilter,
    ]);

    // ---------------------------------------------------------
    // TOTALS
    // ---------------------------------------------------------

    const totals = useMemo(() => {
        return filteredPractices.reduce(
            (acc, practice) => {
                acc.bookings += 1;
                acc.courts += practice.numberOfCourts ?? 0;
                acc.players += practice.numberOfPlayers ?? 0;
                acc.minutes += getMinutesNumericValue(
                    practice.numberOfMinuts
                );

                return acc;
            },
            {
                bookings: 0,
                courts: 0,
                players: 0,
                minutes: 0,
            }
        );
    }, [filteredPractices]);

    // ---------------------------------------------------------
    // GROUP BY DATE + TIME
    // ---------------------------------------------------------

    const groupedPractices = useMemo(() => {
        const map = new Map<string, GroupedPractice>();

        filteredPractices.forEach((practice) => {
            const date = normalizeDate(practice.practiceDate);
            const time = practice.practiceTime;

            const key = `${date}-${time ?? "none"}`;

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    date,
                    timeValue: time,
                    timeText: getPracticeTimeText(time),
                    bookings: [],
                    totalCourts: 0,
                    totalPlayers: 0,
                });
            }

            const group = map.get(key)!;

            group.bookings.push(practice);
            group.totalCourts += practice.numberOfCourts ?? 0;
            group.totalPlayers += practice.numberOfPlayers ?? 0;
        });

        return Array.from(map.values()).sort((a, b) => {
            if (a.date !== b.date) {
                return a.date.localeCompare(b.date);
            }

            return (a.timeValue ?? 0) - (b.timeValue ?? 0);
        });
    }, [filteredPractices, practiceTimes]);

    // ---------------------------------------------------------
    // COUNTRY SUMMARY
    // ---------------------------------------------------------

    const countrySummary = useMemo(() => {
        const map = new Map<
            string,
            {
                country: string;
                bookings: number;
                courts: number;
                players: number;
            }
        >();

        filteredPractices.forEach((practice) => {
            const country =
                practice.country?.trim() || "Ukendt";

            if (!map.has(country)) {
                map.set(country, {
                    country,
                    bookings: 0,
                    courts: 0,
                    players: 0,
                });
            }

            const item = map.get(country)!;

            item.bookings += 1;
            item.courts += practice.numberOfCourts ?? 0;
            item.players += practice.numberOfPlayers ?? 0;
        });

        return Array.from(map.values()).sort(
            (a, b) => b.courts - a.courts
        );
    }, [filteredPractices]);

    const resetFilters = () => {
        setCountryFilter("");
        setDateFilter(null);
        setTimeFilter(null);
    };

    // ---------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------

    return (
        <div>
                    <HeroSectionV2
                titleEn="Traningplan"
                titleDa="Træningsplan"
                descriptionEn="See training wishesh"
                descriptionDa="Se træning ønsker"
                imageSrc={Billede1}
            />
        <Container size="xl" py="xl">

            <Stack gap="lg">
                <div>
                    <Title order={1}>
                        Træningsbookinger
                    </Title>

                    <Text c="dimmed">
                        Administrativt overblik over ønsker til
                        træningsbaner.
                    </Text>
                </div>

                {error && (
                    <Alert color="red" title="Fejl">
                        {error}
                    </Alert>
                )}

                {/* EVENT */}
                <Paper withBorder p="lg" radius="md">
                    <Group align="end">
                        <Select
                            label="Event"
                            placeholder={
                                loadingEvents
                                    ? "Henter events..."
                                    : "Vælg event"
                            }
                            searchable
                            clearable={false}
                            value={selectedEventId}
                            onChange={setSelectedEventId}
                            data={events.map((event) => ({
                                value: String(event.eventID),
                                label: event.eventName,
                            }))}
                            disabled={loadingEvents}
                            style={{ minWidth: 320 }}
                        />

                        {loadingEvents && (
                            <Loader size="sm" />
                        )}
                    </Group>
                </Paper>

                {/* FILTER */}
                <Paper withBorder p="lg" radius="md">
                    <Stack gap="md">
                        <Group justify="space-between">
                            <div>
                                <Title order={3}>Filtre</Title>

                                <Text size="sm" c="dimmed">
                                    Filtrér bookingerne for hurtigt at
                                    finde bestemte ønsker.
                                </Text>
                            </div>

                            <Button
                                variant="default"
                                onClick={resetFilters}
                            >
                                Nulstil filtre
                            </Button>
                        </Group>

                        <Grid>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <TextInput
                                    label="Land"
                                    placeholder="Fx Denmark, China..."
                                    value={countryFilter}
                                    onChange={(event) =>
                                        setCountryFilter(
                                            event.currentTarget.value
                                        )
                                    }
                                />
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label="Dato"
                                    placeholder="Alle datoer"
                                    clearable
                                    searchable
                                    value={dateFilter}
                                    onChange={setDateFilter}
                                    data={availableDates}
                                />
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label="Tidspunkt"
                                    placeholder="Alle tidspunkter"
                                    clearable
                                    value={timeFilter}
                                    onChange={setTimeFilter}
                                    data={practiceTimes.map((time) => ({
                                        value: String(time.value),
                                        label: time.text,
                                    }))}
                                />
                            </Grid.Col>
                        </Grid>
                    </Stack>
                </Paper>

                {loadingPractices ? (
                    <Paper withBorder p={50}>
                        <Group justify="center">
                            <Loader />
                            <Text>Henter træningsbookinger...</Text>
                        </Group>
                    </Paper>
                ) : (
                    <>
                        {/* KPI */}
                        <SimpleGrid
                            cols={{ base: 2, md: 4 }}
                            spacing="md"
                        >
                            <Card withBorder radius="md" padding="lg">
                                <Text size="sm" c="dimmed">
                                    Bookingønsker
                                </Text>

                                <Text
                                    size="2rem"
                                    fw={700}
                                >
                                    {totals.bookings}
                                </Text>
                            </Card>

                            <Card withBorder radius="md" padding="lg">
                                <Text size="sm" c="dimmed">
                                    Ønskede baner
                                </Text>

                                <Text
                                    size="2rem"
                                    fw={700}
                                >
                                    {totals.courts}
                                </Text>
                            </Card>

                            <Card withBorder radius="md" padding="lg">
                                <Text size="sm" c="dimmed">
                                    Spillere
                                </Text>

                                <Text
                                    size="2rem"
                                    fw={700}
                                >
                                    {totals.players}
                                </Text>
                            </Card>

                            <Card withBorder radius="md" padding="lg">
                                <Text size="sm" c="dimmed">
                                    Lande
                                </Text>

                                <Text
                                    size="2rem"
                                    fw={700}
                                >
                                    {countrySummary.length}
                                </Text>
                            </Card>
                        </SimpleGrid>

                        {/* GROUPED OVERVIEW */}
                        <Paper withBorder p="lg" radius="md">
                            <Stack gap="md">
                                <div>
                                    <Title order={2}>
                                        Overblik pr. tidspunkt
                                    </Title>

                                    <Text size="sm" c="dimmed">
                                        Her kan du hurtigt se den
                                        samlede efterspørgsel på hvert
                                        træningstidspunkt.
                                    </Text>
                                </div>

                                {groupedPractices.length === 0 ? (
                                    <Text c="dimmed">
                                        Ingen bookinger matcher de valgte
                                        filtre.
                                    </Text>
                                ) : (
                                    <SimpleGrid
                                        cols={{
                                            base: 1,
                                            md: 2,
                                            xl: 3,
                                        }}
                                    >
                                        {groupedPractices.map((group) => (
                                            <Card
                                                key={group.key}
                                                withBorder
                                                radius="md"
                                                padding="lg"
                                            >
                                                <Stack gap="sm">
                                                    <Group
                                                        justify="space-between"
                                                        align="flex-start"
                                                    >
                                                        <div>
                                                            <Text
                                                                fw={700}
                                                                size="lg"
                                                            >
                                                                {formatDate(
                                                                    group.date
                                                                )}
                                                            </Text>

                                                            <Text
                                                                fw={600}
                                                                size="xl"
                                                            >
                                                                {
                                                                    group.timeText
                                                                }
                                                            </Text>
                                                        </div>

                                                        <Badge
                                                            size="lg"
                                                            variant="light"
                                                        >
                                                            {
                                                                group.bookings
                                                                    .length
                                                            }{" "}
                                                            ønsker
                                                        </Badge>
                                                    </Group>

                                                    <Group gap="xl">
                                                        <div>
                                                            <Text
                                                                size="xs"
                                                                c="dimmed"
                                                            >
                                                                Baner
                                                            </Text>

                                                            <Text
                                                                fw={700}
                                                                size="xl"
                                                            >
                                                                {
                                                                    group.totalCourts
                                                                }
                                                            </Text>
                                                        </div>

                                                        <div>
                                                            <Text
                                                                size="xs"
                                                                c="dimmed"
                                                            >
                                                                Spillere
                                                            </Text>

                                                            <Text
                                                                fw={700}
                                                                size="xl"
                                                            >
                                                                {
                                                                    group.totalPlayers
                                                                }
                                                            </Text>
                                                        </div>
                                                    </Group>

                                                    <div>
                                                        {group.bookings.map(
                                                            (booking) => (
                                                                <Group
                                                                    key={
                                                                        booking.practiceId
                                                                    }
                                                                    justify="space-between"
                                                                    style={{
                                                                        borderTop:
                                                                            "1px solid #eee",
                                                                        paddingTop: 8,
                                                                        marginTop: 8,
                                                                    }}
                                                                >
                                                                    <Text
                                                                        size="sm"
                                                                        fw={500}
                                                                    >
                                                                        {booking.country ||
                                                                            "—"}
                                                                    </Text>

                                                                    <Text
                                                                        size="sm"
                                                                        c="dimmed"
                                                                    >
                                                                        {booking.numberOfCourts ??
                                                                            0}{" "}
                                                                        baner ·{" "}
                                                                        {booking.numberOfPlayers ??
                                                                            0}{" "}
                                                                        spillere
                                                                    </Text>
                                                                </Group>
                                                            )
                                                        )}
                                                    </div>
                                                </Stack>
                                            </Card>
                                        ))}
                                    </SimpleGrid>
                                )}
                            </Stack>
                        </Paper>

                        {/* COUNTRY SUMMARY */}
                        <Paper withBorder p="lg" radius="md">
                            <Stack gap="md">
                                <div>
                                    <Title order={2}>
                                        Overblik pr. land
                                    </Title>

                                    <Text size="sm" c="dimmed">
                                        Samlet efterspørgsel fra hvert
                                        land.
                                    </Text>
                                </div>

                                <Table
                                    striped
                                    highlightOnHover
                                >
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>
                                                Land
                                            </Table.Th>

                                            <Table.Th>
                                                Ønsker
                                            </Table.Th>

                                            <Table.Th>
                                                Baner
                                            </Table.Th>

                                            <Table.Th>
                                                Spillere
                                            </Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>

                                    <Table.Tbody>
                                        {countrySummary.map((item) => (
                                            <Table.Tr key={item.country}>
                                                <Table.Td>
                                                    <Text fw={600}>
                                                        {item.country}
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td>
                                                    {item.bookings}
                                                </Table.Td>

                                                <Table.Td>
                                                    {item.courts}
                                                </Table.Td>

                                                <Table.Td>
                                                    {item.players}
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Stack>
                        </Paper>

                        {/* DETAILS */}
                        <Paper withBorder p="lg" radius="md">
                            <Stack gap="md">
                                <div>
                                    <Title order={2}>
                                        Alle bookingønsker
                                    </Title>

                                    <Text size="sm" c="dimmed">
                                        {filteredPractices.length} af{" "}
                                        {practices.length} bookinger
                                        vises.
                                    </Text>
                                </div>

                                <div style={{ overflowX: "auto" }}>
                                    <Table
                                        striped
                                        highlightOnHover
                                        withTableBorder
                                    >
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>
                                                    Land
                                                </Table.Th>

                                                <Table.Th>
                                                    Dato
                                                </Table.Th>

                                                <Table.Th>
                                                    Tid
                                                </Table.Th>

                                                <Table.Th>
                                                    Baner
                                                </Table.Th>

                                                <Table.Th>
                                                    Spillere
                                                </Table.Th>

                                                <Table.Th>
                                                    Varighed
                                                </Table.Th>

                                                <Table.Th>
                                                    User ID
                                                </Table.Th>

                                                <Table.Th>
                                                    Oprettet
                                                </Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>

                                        <Table.Tbody>
                                            {filteredPractices.map(
                                                (practice) => (
                                                    <Table.Tr
                                                        key={
                                                            practice.practiceId
                                                        }
                                                    >
                                                        <Table.Td>
                                                            <Text fw={600}>
                                                                {practice.country ||
                                                                    "—"}
                                                            </Text>
                                                        </Table.Td>

                                                        <Table.Td>
                                                            {formatDate(
                                                                practice.practiceDate
                                                            )}
                                                        </Table.Td>

                                                        <Table.Td>
                                                            <Badge
                                                                variant="light"
                                                            >
                                                                {getPracticeTimeText(
                                                                    practice.practiceTime
                                                                )}
                                                            </Badge>
                                                        </Table.Td>

                                                        <Table.Td>
                                                            {practice.numberOfCourts ??
                                                                "—"}
                                                        </Table.Td>

                                                        <Table.Td>
                                                            {practice.numberOfPlayers ??
                                                                "—"}
                                                        </Table.Td>

                                                        <Table.Td>
                                                            {getPracticeMinutesText(
                                                                practice.numberOfMinuts
                                                            )}
                                                        </Table.Td>

                                                        <Table.Td>
                                                            {practice.userId ??
                                                                "—"}
                                                        </Table.Td>

                                                        <Table.Td>
                                                            {practice.createdDate
                                                                ? new Date(
                                                                    practice.createdDate
                                                                ).toLocaleString(
                                                                    "da-DK"
                                                                )
                                                                : "—"}
                                                        </Table.Td>
                                                    </Table.Tr>
                                                )
                                            )}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                            </Stack>
                        </Paper>
                    </>
                )}
            </Stack>
            </Container>
        </div>
    );
};

export default FormPracticeAdminPage;